(() => {
  'use strict';

  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => [...c.querySelectorAll(s)];
  const i18n = window.euvaroTranslations || {};
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const backdrop = qs('[data-drawer-backdrop]');
  let lastFocused = null;

  const reveal = (context = document) => {
    const els = qsa('.reveal:not(.visible)', context);
    if (!els.length) return;
    if (reduced || !('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('visible'));
      return;
    }
    const io = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        io.unobserve(entry.target);
      }
    }), { rootMargin: '0px 0px -40px', threshold: 0.08 });
    els.forEach(el => io.observe(el));
  };
  reveal();

  // Randomize the homepage discovery feed on every full page load.
  // We render up to 50 candidates in Liquid and keep a random subset visible.
  const randomizeProductFeeds = root => {
    qsa('[data-random-product-feed]', root || document).forEach(grid => {
      const items = qsa('[data-random-feed-item]', grid);
      if (!items.length) return;

      const requested = Number.parseInt(grid.dataset.randomProductCount || '24', 10);
      const visibleCount = Number.isFinite(requested) && requested > 0
        ? Math.min(requested, items.length)
        : Math.min(24, items.length);

      // Fisher-Yates gives an unbiased shuffle, unlike sort(() => Math.random() - .5).
      const shuffled = items.slice();
      for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }

      const visible = new Set(shuffled.slice(0, visibleCount));
      items.forEach(item => {
        item.hidden = !visible.has(item);
      });

      // Reorder visible cards so each load also gets a fresh sequence.
      shuffled.slice(0, visibleCount).forEach(item => grid.appendChild(item));
      grid.dataset.randomFeedReady = 'true';
    });
  };
  randomizeProductFeeds(document);

  const syncHeader = () => {
    const header = qs('[data-site-header]');
    if (header) header.classList.toggle('scrolled', window.scrollY > 12);
  };
  syncHeader();
  window.addEventListener('scroll', syncHeader, { passive: true });

  // Keep only the theme cart in the header. External body-level floating carts
  // are hidden by desktop CSS, but are restored for mobile by the media query.
  // Observe child insertion only: never observe style/class while writing styles.
  const desktopCartMedia = window.matchMedia('(min-width: 901px)');
  const duplicateCartSelector = 'body > .floating-cart,body > .sticky-cart,body > .cart-floating,body > [data-floating-cart],body > [data-sticky-cart]';
  const syncHeaderCart = () => {
    const cart = qs('[data-header-cart]');
    const host = qs('.header-actions');
    if (cart && host && cart.parentElement !== host) host.appendChild(cart);

    qsa(duplicateCartSelector).forEach(el => {
      if (el === cart) return;
      if (desktopCartMedia.matches) {
        el.dataset.marketplaceDesktopCartHidden = 'true';
        el.setAttribute('aria-hidden', 'true');
      } else if (el.dataset.marketplaceDesktopCartHidden === 'true') {
        delete el.dataset.marketplaceDesktopCartHidden;
        el.removeAttribute('aria-hidden');
      }
    });
  };
  syncHeaderCart();
  if (desktopCartMedia.addEventListener) desktopCartMedia.addEventListener('change', syncHeaderCart);
  else if (desktopCartMedia.addListener) desktopCartMedia.addListener(syncHeaderCart);
  const cartObserver = new MutationObserver(() => window.requestAnimationFrame(syncHeaderCart));
  cartObserver.observe(document.body, { childList: true });

  const setBackdrop = show => {
    if (!backdrop) return;
    if (show) {
      backdrop.hidden = false;
      requestAnimationFrame(() => backdrop.classList.add('active'));
    } else {
      backdrop.classList.remove('active');
      window.setTimeout(() => {
        if (!backdrop.classList.contains('active')) backdrop.hidden = true;
      }, 250);
    }
  };

  const activeDrawer = () => qs('[data-drawer].active');
  const filterPanel = () => qs('[data-filter-panel]');

  const closeFilters = () => {
    const panel = filterPanel();
    if (!panel) return;
    panel.classList.remove('active');
    if (window.matchMedia('(max-width: 900px)').matches) panel.setAttribute('aria-hidden', 'true');
    else panel.removeAttribute('aria-hidden');
    qsa('[data-filter-open]').forEach(b => b.setAttribute('aria-expanded', 'false'));
  };

  const closeDrawers = () => {
    qsa('[data-drawer]').forEach(d => {
      d.classList.remove('active');
      d.setAttribute('aria-hidden', 'true');
    });
  };

  const closeOverlays = ({ restoreFocus = true } = {}) => {
    closeDrawers();
    closeFilters();
    setBackdrop(false);
    document.body.classList.remove('drawer-open');
    if (restoreFocus && lastFocused && document.contains(lastFocused)) lastFocused.focus();
    lastFocused = null;
  };

  const openDrawer = (name, trigger) => {
    const drawer = qs(`[data-drawer="${name}"]`);
    if (!drawer) return;
    closeOverlays({ restoreFocus: false });
    lastFocused = trigger || document.activeElement;
    drawer.classList.add('active');
    drawer.setAttribute('aria-hidden', 'false');
    setBackdrop(true);
    document.body.classList.add('drawer-open');
    requestAnimationFrame(() => (qs('button, a, input, select, [tabindex]:not([tabindex="-1"])', drawer) || drawer).focus());
  };

  const openFilters = trigger => {
    const panel = filterPanel();
    if (!panel) return;
    closeOverlays({ restoreFocus: false });
    lastFocused = trigger || document.activeElement;
    panel.classList.add('active');
    panel.setAttribute('aria-hidden', 'false');
    qsa('[data-filter-open]').forEach(b => b.setAttribute('aria-expanded', 'true'));
    setBackdrop(true);
    document.body.classList.add('drawer-open');
    requestAnimationFrame(() => (qs('button, input, select, a', panel) || panel).focus());
  };

  const syncResponsiveFilters = () => {
    const panel = filterPanel();
    if (!panel) return;
    if (!window.matchMedia('(max-width: 900px)').matches) {
      panel.classList.remove('active');
      panel.removeAttribute('aria-hidden');
      qsa('[data-filter-open]').forEach(b => b.setAttribute('aria-expanded', 'false'));
      if (!activeDrawer()) {
        document.body.classList.remove('drawer-open');
        setBackdrop(false);
      }
    } else if (!panel.classList.contains('active')) {
      panel.setAttribute('aria-hidden', 'true');
    }
  };
  syncResponsiveFilters();
  window.addEventListener('resize', syncResponsiveFilters, { passive: true });

  document.addEventListener('click', e => {
    const open = e.target.closest('[data-drawer-open]');
    if (open) {
      e.preventDefault();
      openDrawer(open.dataset.drawerOpen, open);
      return;
    }
    const filterOpen = e.target.closest('[data-filter-open]');
    if (filterOpen) {
      e.preventDefault();
      openFilters(filterOpen);
      return;
    }
    if (e.target.closest('[data-drawer-close], [data-filter-close]') || e.target === backdrop) {
      e.preventDefault();
      closeOverlays();
    }
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape') {
      if (activeDrawer() || filterPanel()?.classList.contains('active')) closeOverlays();
      return;
    }
    if (e.key !== 'Tab') return;
    const container = activeDrawer() || (filterPanel()?.classList.contains('active') ? filterPanel() : null);
    if (!container) return;
    const focusables = qsa('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])', container)
      .filter(el => !el.hasAttribute('hidden') && el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // Gallery thumbnails.
  document.addEventListener('click', e => {
    const thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;
    const section = thumb.closest('[data-product-section]') || document;
    const main = qs('[data-main-image]', section);
    if (!main) return;
    main.src = thumb.dataset.thumb;
    main.removeAttribute('srcset');
    qsa('.thumb, .euvaro-thumbnail', section).forEach(t => t.classList.remove('active'));
    thumb.classList.add('active');
  });

  // Preserve every active filter/query parameter while changing sort order.
  document.addEventListener('change', e => {
    const select = e.target.closest('[data-preserve-sort]');
    if (!select) return;
    const url = new URL(window.location.href);
    if (select.value) url.searchParams.set('sort_by', select.value);
    else url.searchParams.delete('sort_by');
    url.searchParams.delete('page');
    window.location.assign(url.toString());
  });

  const setProductLoading = (section, loading) => {
    section?.classList.toggle('is-variant-loading', loading);
    qsa('input[data-option-value-id],button[data-add-to-cart]', section || document).forEach(el => {
      if (loading) {
        el.dataset.wasDisabled = el.disabled ? 'true' : 'false';
        el.disabled = true;
      } else if (el.dataset.wasDisabled === 'false') {
        el.disabled = false;
        delete el.dataset.wasDisabled;
      }
    });
  };

  // Shopify high-variant safe option picker. The server re-renders the product
  // section for the selected option-value IDs, so price/media/availability stay
  // correct without iterating through every product.variant in Liquid.
  let variantRequest = 0;
  document.addEventListener('change', async e => {
    const input = e.target.closest('input[data-option-value-id]');
    if (!input) return;

    const section = input.closest('[data-product-section]');
    const picker = input.closest('[data-option-value-selectors]');
    if (!section || !picker) return;

    const sectionId = section.dataset.sectionId;
    const selected = qsa('input[data-option-value-id]:checked', picker);
    const optionValueIds = selected.map(el => el.dataset.optionValueId).filter(Boolean);
    const selectedProductUrl = input.dataset.productUrl || section.dataset.productUrl || window.location.pathname;
    const requestUrl = new URL(selectedProductUrl, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    if (optionValueIds.length) requestUrl.searchParams.set('option_values', optionValueIds.join(','));

    const requestId = ++variantRequest;
    setProductLoading(section, true);

    try {
      const res = await fetch(requestUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!res.ok) throw new Error(`${i18n.variantLoadError || 'Could not load variant'} (${res.status})`);
      const html = await res.text();
      if (requestId !== variantRequest) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = qs('[data-product-section]', doc);
      if (!fresh) throw new Error('Productsectie ontbreekt in Shopify response');

      const currentAppBlocks = qs('[data-app-blocks]', section);
      const freshAppBlocks = qs('[data-app-blocks]', fresh);
      if (currentAppBlocks && freshAppBlocks) freshAppBlocks.replaceWith(currentAppBlocks);

      section.replaceWith(fresh);
      reveal(fresh);

      const visibleUrl = new URL(selectedProductUrl, window.location.origin);
      const variantId = fresh.dataset.selectedVariantId;
      visibleUrl.searchParams.delete('section_id');
      visibleUrl.searchParams.delete('option_values');
      if (variantId) visibleUrl.searchParams.set('variant', variantId);
      else if (optionValueIds.length) visibleUrl.searchParams.set('option_values', optionValueIds.join(','));
      window.history.replaceState({}, '', visibleUrl);
    } catch (err) {
      console.error('[EUVARO] Variant error:', err);
      setProductLoading(section, false);
      const fallback = new URL(selectedProductUrl, window.location.origin);
      if (optionValueIds.length) fallback.searchParams.set('option_values', optionValueIds.join(','));
      window.location.assign(fallback.toString());
    }
  });

  // Legacy single-select fallback for products rendered by an older cached section.
  const syncLegacyVariant = select => {
    if (!select) return;
    const option = select.options[select.selectedIndex];
    if (!option) return;
    const section = select.closest('[data-product-section]') || document;
    const price = qs('[data-product-price]', section);
    const compareWrap = qs('[data-product-compare-wrap]', section);
    const compare = qs('[data-product-compare]', section);
    const addButton = qs('[data-add-to-cart]', section);
    const addText = qs('[data-add-to-cart-text]', section);
    const mainImage = qs('[data-main-image]', section);
    const available = option.dataset.available === 'true';
    const onSale = option.dataset.onSale === 'true';
    if (price && option.dataset.price) price.textContent = option.dataset.price;
    if (compareWrap) compareWrap.hidden = !onSale;
    if (compare && option.dataset.comparePrice) compare.textContent = option.dataset.comparePrice;
    if (addButton) addButton.disabled = !available;
    if (addText) addText.textContent = available ? (i18n.addToCart || 'Add to cart') : (i18n.soldOut || 'Sold out');
    if (mainImage && option.dataset.image) {
      mainImage.src = option.dataset.image;
      mainImage.removeAttribute('srcset');
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', option.value);
    window.history.replaceState({}, '', url);
  };
  document.addEventListener('change', e => {
    const select = e.target.closest('[data-variant-select]');
    if (select) syncLegacyVariant(select);
  });

  let toastTimer;
  const toast = message => {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('show'), 2600);
  };

  const syncCartCount = async () => {
    try {
      const res = await fetch(window.marketplaceRoutes.cart, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      qsa('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
    } catch (_) {}
  };

  document.addEventListener('submit', async e => {
    const form = e.target.closest('[data-quick-add]');
    if (!form) return;
    e.preventDefault();
    if (form.dataset.busy === 'true') return;
    form.dataset.busy = 'true';
    const button = qs('button', form);
    button?.classList.add('loading');
    if (button) button.disabled = true;
    try {
      const res = await fetch(window.marketplaceRoutes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      if (!res.ok) {
        let message = 'Toevoegen lukt niet. Probeer opnieuw.';
        try { const err = await res.json(); if (err?.description) message = err.description; } catch (_) {}
        throw new Error(message);
      }
      await syncCartCount();
      toast(i18n.addedToCart || 'Added to your cart');
    } catch (err) {
      toast(err?.message || i18n.addError || 'Could not add this item.');
    } finally {
      form.dataset.busy = 'false';
      button?.classList.remove('loading');
      if (button) button.disabled = false;
    }
  });

  const loadRecommendations = context => {
    qsa('[data-recommendations][data-url]', context || document).forEach(rec => {
      if (rec.dataset.loaded === 'true') return;
      rec.dataset.loaded = 'true';
      fetch(rec.dataset.url)
        .then(r => r.ok ? r.text() : Promise.reject())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = qs('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim()) {
            rec.innerHTML = fresh.innerHTML;
            reveal(rec);
          } else {
            rec.remove();
          }
        })
        .catch(() => rec.remove());
    });
  };
  loadRecommendations(document);

  document.addEventListener('shopify:section:load', e => {
    reveal(e.target);
    loadRecommendations(e.target);
    syncHeader();
    syncHeaderCart();
    syncResponsiveFilters();
  });
})();
